import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useDesignSystem } from "../../lib/design/designSystem";
import clsx from "clsx";
import { getOptionTitle } from "./selectOptionText";

/**
 * GLASS EFFECT UI COMPONENTS
 *
 * Reusable components implementing the glass morphism design system
 * Based on CorbeilleSettings design patterns
 */

// Re-export specialized components
export { default as GlassDatePicker } from "./GlassDatePicker";
export { default as GlassSearchableSelect } from "./GlassSearchableSelect";
export { default as GlassSelectWithSearch } from "./GlassSelectWithSearch";

const mergeRefs = <T,>(...refs: Array<React.Ref<T> | undefined>) => {
  return (instance: T | null) => {
    refs.forEach((ref) => {
      if (!ref) return;
      if (typeof ref === "function") {
        ref(instance);
      } else {
        try {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore - mutable ref assignment
          ref.current = instance;
        } catch (error) {
          // Ignore assignment errors for immutable refs
        }
      }
    });
  };
};

// ============================================================================
// BUTTON COMPONENTS
// ============================================================================

interface GlassButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?:
    | "primary"
    | "secondary"
    | "success"
    | "danger"
    | "warning"
    | "ghost";
  size?: "xs" | "sm" | "md" | "lg";
  loading?: boolean;
  iconOnly?: boolean;
  children: React.ReactNode;
}

export const GlassButton: React.FC<GlassButtonProps> = ({
  variant = "primary",
  size = "md",
  loading = false,
  iconOnly = false,
  className,
  children,
  disabled,
  ...props
}) => {
  const { components } = useDesignSystem();

  const getButtonClass = () => {
    // Icon-only buttons
    if (iconOnly) {
      const iconSizeMap = {
        xs: components.iconButtonXs,
        sm: components.iconButtonSmall,
        md: components.iconButton,
        lg: components.iconButtonLarge,
      };
      return iconSizeMap[size];
    }

    // Regular buttons with variants and sizes
    const buttonMap = {
      primary: {
        xs: components.primaryButtonXs,
        sm: components.primaryButtonSmall,
        md: components.primaryButton,
        lg: components.primaryButtonLarge,
      },
      secondary: {
        xs: components.secondaryButtonXs,
        sm: components.secondaryButtonSmall,
        md: components.secondaryButton,
        lg: components.secondaryButtonLarge,
      },
      success: {
        xs: components.successButtonXs,
        sm: components.successButtonSmall,
        md: components.successButton,
        lg: components.successButtonLarge,
      },
      danger: {
        xs: components.dangerButtonXs,
        sm: components.dangerButtonSmall,
        md: components.dangerButton,
        lg: components.dangerButtonLarge,
      },
      warning: {
        xs: components.warningButtonXs,
        sm: components.warningButtonSmall,
        md: components.warningButton,
        lg: components.warningButtonLarge,
      },
      ghost: {
        xs: components.ghostButtonXs,
        sm: components.ghostButtonSmall,
        md: components.ghostButton,
        lg: components.ghostButtonLarge,
      },
    };

    return buttonMap[variant][size];
  };

  const spinnerSizeMap = {
    xs: components.spinnerXs,
    sm: components.spinnerSmall,
    md: components.spinnerMedium,
    lg: components.spinnerLarge,
  };

  return (
    <button
      className={clsx(getButtonClass(), className)}
      disabled={disabled || loading}
      {...props}
    >
      {loading && <div className={clsx(spinnerSizeMap[size], "me-1")} />}
      {children}
    </button>
  );
};

// ============================================================================
// CARD COMPONENTS
// ============================================================================

interface GlassCardProps {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  actions?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  variant?: "default" | "strong";
  hover?: boolean;
}

export const GlassCard: React.FC<GlassCardProps> = ({
  title,
  subtitle,
  description,
  icon,
  actions,
  children,
  className,
  variant = "default",
  hover = false,
}) => {
  const { surfaces, typography } = useDesignSystem();

  return (
    <div
      className={clsx(
        variant === "strong" ? surfaces.cardStrong : surfaces.card,
        hover && surfaces.cardHover,
        className
      )}
    >
      {(title || actions) && (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between mb-3">
          <div className="flex items-start gap-2">
            {icon && (
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-white">
                {icon}
              </div>
            )}
            <div className="space-y-1">
              {title && <h3 className={typography.cardTitle}>{title}</h3>}
              {subtitle && (
                <div className={typography.subtitle}>{subtitle}</div>
              )}
              {description && (
                <p className={typography.description}>{description}</p>
              )}
            </div>
          </div>
          {actions && <div className="flex items-center gap-3">{actions}</div>}
        </div>
      )}
      {children}
    </div>
  );
};

// ============================================================================
// BADGE COMPONENTS
// ============================================================================

interface GlassBadgeProps {
  variant?:
    | "primary"
    | "secondary"
    | "success"
    | "danger"
    | "warning"
    | "info"
    | "neutral";
  children: React.ReactNode;
  className?: string;
}

export const GlassBadge: React.FC<GlassBadgeProps> = ({
  variant = "secondary",
  children,
  className,
}) => {
  const { components } = useDesignSystem();

  const variantClasses = {
    primary: components.badge.primary,
    secondary: components.badge.secondary,
    success: components.badge.success,
    danger: components.badge.danger,
    warning: components.badge.warning,
    info: components.badge.info,
    neutral: components.badge.neutral,
  };

  return (
    <span
      className={clsx(
        components.badge.base,
        variantClasses[variant],
        className
      )}
    >
      {children}
    </span>
  );
};

// ============================================================================
// INPUT COMPONENTS
// ============================================================================

interface GlassInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> {
  label?: string;
  error?: string;
  helperText?: string;
  size?: "xs" | "sm" | "md" | "lg";
}

export const GlassInput = React.forwardRef<HTMLInputElement, GlassInputProps>(
  (
    { label, error, helperText, size = "md", className, disabled, ...props },
    ref
  ) => {
    const { surfaces, typography } = useDesignSystem();

    const sizeClasses = {
      xs: "px-2 py-1 text-xs",
      sm: "px-2.5 py-1.5 text-xs",
      md: "px-3 py-2 text-sm",
      lg: "px-4 py-2.5 text-base",
    };

    return (
      <div className="space-y-1">
        {label && <label className={typography.label}>{label}</label>}
        <input
          ref={ref}
          className={clsx(
            "w-full rounded-lg border border-white/10 bg-white/5 backdrop-blur transition-all duration-200",
            "text-white placeholder:text-white/40",
            "hover:border-white/20 hover:bg-white/10",
            "focus:border-primary-500/50 focus:bg-white/10 focus:outline-none",
            sizeClasses[size],
            disabled && surfaces.inputDisabled,
            error && "border-rose-500/50 focus:border-rose-500/70",
            className
          )}
          disabled={disabled}
          {...props}
        />
        {error && <p className="text-xs text-rose-300">{error}</p>}
        {helperText && !error && (
          <p className={clsx(typography.caption)}>{helperText}</p>
        )}
      </div>
    );
  }
);

GlassInput.displayName = "GlassInput";

// ============================================================================
// SELECT COMPONENTS
// ============================================================================

interface GlassSelectProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, "size"> {
  label?: string;
  error?: string;
  helperText?: string;
  containerClassName?: string;
  size?: "xs" | "sm" | "md" | "lg";
  placeholder?: string;
  /**
   * Minimum width (px) of the dropdown menu. Defaults to the trigger's width.
   * Same API as `GlassSelectWithSearch`: set it when the trigger is compact
   * but option labels are long.
   */
  menuMinWidth?: number;
  /** Let option labels wrap instead of being truncated (same as `GlassSelectWithSearch`). */
  wrapOptionLabels?: boolean;
}

type GlassSelectOption = {
  value: string;
  label: React.ReactNode;
  disabled: boolean;
  /** Full text of the option (`title` attribute, else its text) for tooltips. */
  title?: string;
  /** Compact trigger label from `data-trigger-label`, when given. */
  triggerLabel?: string;
};

const optionHeightMap: Record<Required<GlassSelectProps>["size"], number> = {
  xs: 32,
  sm: 36,
  md: 40,
  lg: 48,
};

const GlassSelectSingle = React.forwardRef<HTMLSelectElement, GlassSelectProps>(
  (
    {
      label,
      error,
      helperText,
      containerClassName,
      size = "md",
      className,
      disabled,
      children,
      placeholder,
      value,
      defaultValue,
      onChange,
      onFocus,
      onBlur,
      id,
      multiple: _ignoredMultiple,
      menuMinWidth,
      wrapOptionLabels = false,
      ...restProps
    },
    ref
  ) => {
    const { surfaces, typography } = useDesignSystem();
    const { t } = useTranslation("common");
    const generatedId = useId();
    const selectId = id ?? `${generatedId}-select`;
    const labelId = label ? `${selectId}-label` : undefined;
    const listboxIdValue = `${selectId}-listbox`;
    const selectRef = useRef<HTMLSelectElement | null>(null);
    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const menuRef = useRef<HTMLDivElement | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [menuPosition, setMenuPosition] = useState<{
      top: number;
      left: number;
      width: number;
    } | null>(null);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);

    const options = useMemo<GlassSelectOption[]>(() => {
      const collected: GlassSelectOption[] = [];

      const collect = (nodes: React.ReactNode) => {
        React.Children.forEach(nodes, (child) => {
          if (!React.isValidElement(child)) return;

          const element = child as React.ReactElement<{
            children?: React.ReactNode;
            value?: unknown;
            disabled?: boolean;
            title?: string;
            "data-trigger-label"?: string;
          }>;
          const { type, props } = element;

          if (type === React.Fragment) {
            collect(props.children);
            return;
          }

          if (typeof type === "string" && type === "optgroup") {
            collect(props.children);
            return;
          }

          if (typeof type === "string" && type === "option") {
            const optionValue =
              props.value ??
              (typeof props.children === "string" ? props.children : "");
            collected.push({
              value: optionValue != null ? String(optionValue) : "",
              label: props.children,
              disabled: Boolean(props.disabled),
              title: getOptionTitle(props.title, props.children),
              triggerLabel: props["data-trigger-label"] || undefined,
            });
          }
        });
      };

      collect(children);
      return collected;
    }, [children]);

    const isControlled = value !== undefined;

    const [uncontrolledValue, setUncontrolledValue] = useState<string>(() => {
      if (value !== undefined && value !== null) {
        return Array.isArray(value) ? value[0] ?? "" : String(value);
      }
      if (defaultValue !== undefined && defaultValue !== null) {
        return Array.isArray(defaultValue)
          ? defaultValue[0] ?? ""
          : String(defaultValue);
      }
      if (placeholder !== undefined) return "";
      return options[0]?.value ?? "";
    });

    useEffect(() => {
      if (isControlled) return;
      if (defaultValue !== undefined && defaultValue !== null) {
        setUncontrolledValue(
          Array.isArray(defaultValue)
            ? defaultValue[0] ?? ""
            : String(defaultValue)
        );
      }
    }, [defaultValue, isControlled]);

    useEffect(() => {
      if (isControlled) return;
      if (!options.some((option) => option.value === uncontrolledValue)) {
        setUncontrolledValue(options[0]?.value ?? "");
      }
    }, [isControlled, options, uncontrolledValue]);

    const currentValueRaw = isControlled ? value : uncontrolledValue;
    const normalizedCurrentValue = Array.isArray(currentValueRaw)
      ? currentValueRaw[0] ?? ""
      : currentValueRaw != null
      ? String(currentValueRaw)
      : "";

    useEffect(() => {
      if (!selectRef.current) return;
      selectRef.current.value = normalizedCurrentValue;
    }, [normalizedCurrentValue]);

    const selectedOption = useMemo(
      () =>
        options.find((option) => option.value === normalizedCurrentValue) ??
        null,
      [options, normalizedCurrentValue]
    );

    const optionHeight = optionHeightMap[size] ?? optionHeightMap.md;

    const computeMenuPosition = useCallback(() => {
      if (!buttonRef.current) return;
      const rect = buttonRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const rowHeight = wrapOptionLabels ? optionHeight + 18 : optionHeight;
      const estimatedHeight =
        Math.min(Math.max(options.length, 1), 6) * rowHeight + 16;
      let top = rect.bottom + 8;
      if (top + estimatedHeight > viewportHeight - 8) {
        top = Math.max(8, rect.top - estimatedHeight);
      }
      // At least as wide as the trigger, optionally wider (menuMinWidth), never
      // wider than the viewport.
      const width = Math.min(
        viewportWidth - 16,
        Math.max(rect.width, menuMinWidth ?? 0)
      );
      let left = rect.left;
      if (left + width > viewportWidth - 8) {
        left = Math.max(8, viewportWidth - width - 8);
      }
      setMenuPosition({ top, left, width });
    }, [optionHeight, options.length, menuMinWidth, wrapOptionLabels]);

    const closeMenu = useCallback((focusButton = false) => {
      setIsOpen(false);
      setMenuPosition(null);
      if (focusButton) {
        requestAnimationFrame(() => {
          buttonRef.current?.focus();
        });
      }
    }, []);

    const dispatchFocusEvent = useCallback((type: "focus" | "blur") => {
      const element = selectRef.current;
      if (!element) return;
      try {
        const event =
          typeof FocusEvent === "function"
            ? new FocusEvent(type, { bubbles: type === "focus" })
            : new Event(type, { bubbles: type === "focus" });
        element.dispatchEvent(event);
      } catch {
        element.dispatchEvent(new Event(type, { bubbles: type === "focus" }));
      }
    }, []);

    const handleNativeChange = useCallback(
      (event: React.ChangeEvent<HTMLSelectElement>) => {
        if (!isControlled) {
          setUncontrolledValue(event.target.value);
        }
        onChange?.(event);
      },
      [isControlled, onChange]
    );

    const getNextEnabledIndex = useCallback(
      (startIndex: number, direction: 1 | -1) => {
        if (!options.length) return -1;
        for (let offset = 1; offset <= options.length; offset += 1) {
          const index =
            (startIndex + direction * offset + options.length) % options.length;
          if (!options[index]?.disabled) {
            return index;
          }
        }
        return options.findIndex((option) => !option.disabled);
      },
      [options]
    );

    useEffect(() => {
      if (!options.length) {
        setHighlightedIndex(-1);
        return;
      }

      const currentIndex = options.findIndex(
        (option) => option.value === normalizedCurrentValue
      );
      if (currentIndex >= 0 && !options[currentIndex]?.disabled) {
        setHighlightedIndex(currentIndex);
        return;
      }

      const nextIndex = getNextEnabledIndex(-1, 1);
      setHighlightedIndex(nextIndex >= 0 ? nextIndex : -1);
    }, [getNextEnabledIndex, normalizedCurrentValue, options]);

    const handleOptionSelect = useCallback(
      (option: GlassSelectOption) => {
        if (disabled || option.disabled) return;
        if (!selectRef.current) return;
        selectRef.current.value = option.value;
        selectRef.current.dispatchEvent(new Event("change", { bubbles: true }));
        closeMenu(true);
      },
      [closeMenu, disabled]
    );

    const openMenu = useCallback(() => {
      if (disabled || !options.length) return;
      computeMenuPosition();
      setIsOpen(true);
    }, [computeMenuPosition, disabled, options.length]);

    useEffect(() => {
      if (!isOpen) return;
      computeMenuPosition();

      const handleClickOutside = (event: MouseEvent) => {
        if (
          menuRef.current?.contains(event.target as Node) ||
          buttonRef.current?.contains(event.target as Node)
        ) {
          return;
        }
        closeMenu(false);
      };

      const handleWindowScroll = () => computeMenuPosition();

      window.addEventListener("mousedown", handleClickOutside);
      window.addEventListener("resize", handleWindowScroll);
      window.addEventListener("scroll", handleWindowScroll, true);

      return () => {
        window.removeEventListener("mousedown", handleClickOutside);
        window.removeEventListener("resize", handleWindowScroll);
        window.removeEventListener("scroll", handleWindowScroll, true);
      };
    }, [closeMenu, computeMenuPosition, isOpen]);

    useEffect(() => {
      if (!isOpen || !menuRef.current || highlightedIndex < 0) return;
      const activeItem = menuRef.current.querySelector<HTMLElement>(
        `[data-option-index="${highlightedIndex}"]`
      );
      activeItem?.scrollIntoView({ block: "nearest" });
    }, [highlightedIndex, isOpen]);

    const sizeClasses = {
      xs: "px-2 py-1 text-xs",
      sm: "px-2.5 py-1.5 text-xs",
      md: "px-3 py-2 text-sm",
      lg: "px-4 py-2.5 text-base",
    };

    const showPlaceholder =
      placeholder !== undefined &&
      (normalizedCurrentValue === "" || !selectedOption);

    const displayNode = showPlaceholder ? (
      <span className="text-white/40">{placeholder}</span>
    ) : (
      selectedOption?.triggerLabel ?? selectedOption?.label ?? (
        <span className="text-white/40">{placeholder ?? t("actions.select")}</span>
      )
    );

    const triggerTitle = showPlaceholder ? placeholder : selectedOption?.title;

    const buttonClasses = clsx(
      "flex w-full items-center justify-between gap-2 rounded-lg border border-white/10",
      "bg-white/5 text-start text-white backdrop-blur transition-all duration-200",
      !disabled && "cursor-pointer hover:border-white/20 hover:bg-white/10",
      "focus-visible:border-primary-500/50 focus-visible:bg-white/10 focus-visible:outline-none",
      sizeClasses[size],
      disabled && surfaces.inputDisabled,
      error && "border-rose-500/50 focus-visible:border-rose-500/70",
      className
    );

    const handleButtonClick = () => {
      if (disabled || !options.length) return;
      if (isOpen) {
        closeMenu(false);
      } else {
        openMenu();
      }
    };

    const handleButtonKeyDown = (
      event: React.KeyboardEvent<HTMLButtonElement>
    ) => {
      if (disabled) return;

      const moveHighlight = (direction: 1 | -1) => {
        if (!options.length) return;
        const nextIndex = getNextEnabledIndex(
          highlightedIndex >= 0
            ? highlightedIndex
            : direction === 1
            ? -1
            : options.length,
          direction
        );
        if (nextIndex >= 0) {
          setHighlightedIndex(nextIndex);
        }
      };

      if (event.key === "ArrowDown") {
        event.preventDefault();
        if (!isOpen) {
          openMenu();
          const currentIndex = options.findIndex(
            (option) => option.value === normalizedCurrentValue
          );
          const nextIndex = getNextEnabledIndex(
            currentIndex >= 0 ? currentIndex : -1,
            1
          );
          if (nextIndex >= 0) {
            setHighlightedIndex(nextIndex);
          }
        } else {
          moveHighlight(1);
        }
        return;
      }

      if (event.key === "ArrowUp") {
        event.preventDefault();
        if (!isOpen) {
          openMenu();
          const currentIndex = options.findIndex(
            (option) => option.value === normalizedCurrentValue
          );
          const nextIndex = getNextEnabledIndex(
            currentIndex >= 0 ? currentIndex : options.length,
            -1
          );
          if (nextIndex >= 0) {
            setHighlightedIndex(nextIndex);
          }
        } else {
          moveHighlight(-1);
        }
        return;
      }

      if (event.key === "Home") {
        if (!isOpen) return;
        event.preventDefault();
        const firstIndex = getNextEnabledIndex(-1, 1);
        if (firstIndex >= 0) {
          setHighlightedIndex(firstIndex);
        }
        return;
      }

      if (event.key === "End") {
        if (!isOpen) return;
        event.preventDefault();
        const lastIndex = getNextEnabledIndex(options.length, -1);
        if (lastIndex >= 0) {
          setHighlightedIndex(lastIndex);
        }
        return;
      }

      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        if (isOpen) {
          const option =
            highlightedIndex >= 0 ? options[highlightedIndex] : undefined;
          if (option && !option.disabled) {
            handleOptionSelect(option);
          }
        } else {
          openMenu();
        }
        return;
      }

      if (event.key === "Escape" && isOpen) {
        event.preventDefault();
        closeMenu(true);
      }
    };

    const handleButtonFocus = () => dispatchFocusEvent("focus");
    const handleButtonBlur = () => dispatchFocusEvent("blur");

    return (
      <div className={clsx("space-y-1", containerClassName)}>
        {label && (
          <label id={labelId} htmlFor={selectId} className={typography.label}>
            {label}
          </label>
        )}

        <select
          id={selectId}
          ref={mergeRefs(selectRef, ref)}
          value={normalizedCurrentValue}
          onChange={handleNativeChange}
          onFocus={onFocus}
          onBlur={onBlur}
          disabled={disabled}
          aria-hidden="true"
          tabIndex={-1}
          className="sr-only"
          {...restProps}
          aria-labelledby={labelId}
        >
          {children}
        </select>

        <button
          ref={buttonRef}
          type="button"
          className={buttonClasses}
          onClick={handleButtonClick}
          onKeyDown={handleButtonKeyDown}
          onFocus={handleButtonFocus}
          onBlur={handleButtonBlur}
          aria-haspopup="listbox"
          aria-controls={listboxIdValue}
          aria-expanded={isOpen}
          aria-disabled={disabled}
          aria-invalid={Boolean(error)}
          aria-labelledby={labelId}
        >
          <span className="flex-1 truncate text-start" title={triggerTitle}>
            {displayNode}
          </span>
          <span className="flex items-center text-white/60">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              className="h-4 w-4"
              aria-hidden="true"
            >
              <path d="M5.23 7.21a.75.75 0 011.06.02L10 11.138l3.71-3.907a.75.75 0 111.08 1.04l-4.25 4.475a.75.75 0 01-1.08 0L5.25 8.29a.75.75 0 01-.02-1.08z" />
            </svg>
          </span>
        </button>

        {isOpen && menuPosition
          ? createPortal(
              <div
                ref={menuRef}
                id={listboxIdValue}
                role="listbox"
                aria-activedescendant={
                  highlightedIndex >= 0
                    ? `${listboxIdValue}-option-${highlightedIndex}`
                    : undefined
                }
                className="fixed z-[10000] max-h-72 overflow-y-auto rounded-lg border border-white/15 bg-white/10 px-1 py-1 backdrop-blur-xl backdrop-saturate-150 shadow-xl shadow-black/40"
                style={{
                  top: menuPosition.top,
                  left: menuPosition.left,
                  width: menuPosition.width,
                }}
                aria-labelledby={labelId}
              >
                {options.map((option, index) => {
                  const isSelected = option.value === normalizedCurrentValue;
                  const isHighlighted = index === highlightedIndex;
                  const optionClasses = clsx(
                    "flex w-full justify-between gap-2 rounded-md px-2.5 py-2 text-sm transition-colors",
                    wrapOptionLabels ? "items-start" : "items-center",
                    option.disabled
                      ? "cursor-not-allowed text-white/30"
                      : isHighlighted
                      ? "bg-white/20 text-white"
                      : "text-white/90 hover:bg-white/15",
                    isSelected && !option.disabled && "font-semibold text-white"
                  );

                  return (
                    <button
                      key={`${option.value}-${index}`}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      disabled={option.disabled}
                      className={optionClasses}
                      data-option-index={index}
                      id={`${listboxIdValue}-option-${index}`}
                      tabIndex={-1}
                      onMouseEnter={() => {
                        if (!option.disabled) {
                          setHighlightedIndex(index);
                        }
                      }}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => handleOptionSelect(option)}
                      title={option.title}
                    >
                      <span
                        className={clsx(
                          "text-start",
                          wrapOptionLabels ? "min-w-0 flex-1" : "truncate"
                        )}
                      >
                        {option.label}
                      </span>
                      {isSelected && (
                        <span className="text-primary-200">✓</span>
                      )}
                    </button>
                  );
                })}
              </div>,
              document.body
            )
          : null}

        {error && <p className="text-xs text-rose-300">{error}</p>}
        {helperText && !error && (
          <p className={clsx(typography.caption)}>{helperText}</p>
        )}
      </div>
    );
  }
);

const GlassMultiSelect = React.forwardRef<HTMLSelectElement, GlassSelectProps>(
  (
    {
      label,
      error,
      helperText,
      containerClassName,
      className,
      disabled,
      children,
      id,
      size: _ignoredSize,
      placeholder: _ignoredPlaceholder,
      multiple: _ignoredMultiple,
      menuMinWidth: _ignoredMenuMinWidth,
      wrapOptionLabels: _ignoredWrapOptionLabels,
      ...restProps
    },
    ref
  ) => {
    const { typography } = useDesignSystem();
    const generatedId = useId();
    const selectId = id ?? `${generatedId}-multiselect`;
    const labelId = label ? `${selectId}-label` : undefined;

    return (
      <div className={clsx("space-y-1", containerClassName)}>
        {label && (
          <label id={labelId} htmlFor={selectId} className={typography.label}>
            {label}
          </label>
        )}

        <select
          id={selectId}
          ref={ref}
          multiple
          disabled={disabled}
          className={clsx(
            "w-full rounded-lg border border-white/15 bg-white/10 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition disabled:opacity-60 disabled:cursor-not-allowed",
            className
          )}
          aria-labelledby={labelId}
          {...restProps}
        >
          {children}
        </select>

        {error && <p className="text-xs text-rose-300">{error}</p>}
        {helperText && !error && (
          <p className={clsx(typography.caption)}>{helperText}</p>
        )}
      </div>
    );
  }
);

export const GlassSelect = React.forwardRef<
  HTMLSelectElement,
  GlassSelectProps
>((props, ref) => {
  if (props.multiple) {
    return <GlassMultiSelect {...props} ref={ref} />;
  }
  return <GlassSelectSingle {...props} ref={ref} />;
});

GlassSelectSingle.displayName = "GlassSelectSingle";
GlassMultiSelect.displayName = "GlassMultiSelect";
GlassSelect.displayName = "GlassSelect";

// ============================================================================
// TEXTAREA COMPONENTS
// ============================================================================

interface GlassTextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  helperText?: string;
}

export const GlassTextarea = React.forwardRef<
  HTMLTextAreaElement,
  GlassTextareaProps
>(({ label, error, helperText, className, disabled, ...props }, ref) => {
  const { surfaces, typography } = useDesignSystem();

  return (
    <div className="space-y-1">
      {label && <label className={typography.label}>{label}</label>}
      <textarea
        ref={ref}
        className={clsx(
          surfaces.textarea,
          disabled && surfaces.inputDisabled,
          error && "border-rose-500/50 focus:border-rose-500/70",
          className
        )}
        disabled={disabled}
        {...props}
      />
      {error && <p className="text-xs text-rose-300">{error}</p>}
      {helperText && !error && (
        <p className={clsx(typography.caption)}>{helperText}</p>
      )}
    </div>
  );
});

GlassTextarea.displayName = "GlassTextarea";

// ============================================================================
// LIST ITEM COMPONENT
// ============================================================================

interface GlassListItemProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

export const GlassListItem: React.FC<GlassListItemProps> = ({
  children,
  className,
  ...props
}) => {
  const { surfaces } = useDesignSystem();

  return (
    <div className={clsx(surfaces.listItem, className)} {...props}>
      {children}
    </div>
  );
};

// ============================================================================
// EMPTY STATE COMPONENT
// ============================================================================

interface GlassEmptyStateProps {
  icon?: React.ReactNode;
  title?: string;
  message?: string;
  children?: React.ReactNode;
}

export const GlassEmptyState: React.FC<GlassEmptyStateProps> = ({
  icon,
  title,
  message,
  children,
}) => {
  const { surfaces, typography } = useDesignSystem();

  return (
    <div className={surfaces.emptyState}>
      {icon && <div className="text-white/30">{icon}</div>}
      {title && <h3 className={typography.subtitle}>{title}</h3>}
      {message && <p className={typography.bodyMuted}>{message}</p>}
      {children}
    </div>
  );
};

// ============================================================================
// TABS COMPONENT
// ============================================================================

interface Tab {
  id: string;
  label: string;
  count?: number;
}

interface GlassTabsProps {
  tabs: Tab[];
  activeTab: string;
  onChange: (tabId: string) => void;
  className?: string;
}

export const GlassTabs: React.FC<GlassTabsProps> = ({
  tabs,
  activeTab,
  onChange,
  className,
}) => {
  const { components } = useDesignSystem();

  return (
    <div className={clsx(components.tabs.container, className)}>
      <nav className={components.tabs.nav}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => onChange(tab.id)}
            className={clsx(
              components.tabs.button,
              activeTab === tab.id
                ? components.tabs.buttonActive
                : components.tabs.buttonInactive
            )}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span
                className={clsx(
                  components.tabs.badge,
                  activeTab === tab.id
                    ? components.tabs.badgeActive
                    : components.tabs.badgeInactive
                )}
              >
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </nav>
    </div>
  );
};

// ============================================================================
// ALERT COMPONENT
// ============================================================================

interface GlassAlertProps {
  variant?: "info" | "success" | "warning" | "danger";
  icon?: React.ReactNode;
  title?: string;
  children: React.ReactNode;
  className?: string;
}

export const GlassAlert: React.FC<GlassAlertProps> = ({
  variant = "info",
  icon,
  title,
  children,
  className,
}) => {
  const { surfaces, typography } = useDesignSystem();

  const variantClasses = {
    info: surfaces.info,
    success: surfaces.success,
    warning: surfaces.warning,
    danger: surfaces.danger,
  };

  return (
    <div
      className={clsx(
        "flex items-start gap-3 rounded-2xl border px-2.5 py-3 text-sm",
        variantClasses[variant],
        className
      )}
    >
      {icon && <div className="mt-0.5 text-base">{icon}</div>}
      <div className="space-y-1">
        {title && (
          <div className={clsx(typography.label, "text-white/80")}>{title}</div>
        )}
        <div className="text-white/80 leading-relaxed">{children}</div>
      </div>
    </div>
  );
};

// ============================================================================
// DIVIDER COMPONENT
// ============================================================================

export const GlassDivider: React.FC<{ className?: string }> = ({
  className,
}) => {
  const { components } = useDesignSystem();
  return <div className={clsx(components.divider, className)} />;
};

// ============================================================================
// AVATAR COMPONENT
// ============================================================================

interface GlassAvatarProps {
  name: string;
  size?: "small" | "medium" | "large";
  src?: string;
  className?: string;
}

export const GlassAvatar: React.FC<GlassAvatarProps> = ({
  name,
  size = "medium",
  src,
  className,
}) => {
  const { components } = useDesignSystem();

  const sizeClasses = {
    small: components.avatar.small,
    medium: components.avatar.medium,
    large: components.avatar.large,
  };

  const initials = name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  if (src) {
    return (
      <img
        src={src}
        alt={name}
        className={clsx(
          "rounded-full border border-white/10",
          sizeClasses[size],
          className
        )}
      />
    );
  }

  return (
    <div className={clsx(components.avatar.base, sizeClasses[size], className)}>
      {initials}
    </div>
  );
};

// ============================================================================
// SPINNER/LOADER COMPONENTS
// ============================================================================

interface GlassSpinnerProps {
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  className?: string;
}

export const GlassSpinner: React.FC<GlassSpinnerProps> = ({
  size = "md",
  className,
}) => {
  const { components } = useDesignSystem();

  const sizeClasses = {
    xs: components.spinnerXs,
    sm: components.spinnerSmall,
    md: components.spinnerMedium,
    lg: components.spinnerLarge,
    xl: components.spinnerXl,
  };

  return <div className={clsx(sizeClasses[size], className)} />;
};

interface GlassLoaderProps {
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  text?: string;
  className?: string;
}

export const GlassLoader: React.FC<GlassLoaderProps> = ({
  size = "md",
  text,
  className,
}) => {
  const { typography } = useDesignSystem();

  return (
    <div
      className={clsx(
        "flex flex-col items-center justify-center gap-3",
        className
      )}
    >
      <GlassSpinner size={size} />
      {text && <p className={typography.bodyMuted}>{text}</p>}
    </div>
  );
};
